import { useMemo } from 'react';
import { CollapsibleSection } from '../CollapsibleSection';
import { TooltipLabel } from '../TooltipLabel';
import { BenefitsBeginAnchor } from '../BenefitsBeginAnchor';
import { HeaderTooltip } from '../projectionTable/HeaderTooltip';
import { currency } from '../projectionTable/formatters';
import { formatValue } from '../../glossary/formatValue';
import {
  buildPremiumCashflowSchedule,
  PremiumCashflowRow,
  PremiumCashflowSchedule,
} from './buildPremiumCashflowSchedule';

// Monthly rates can carry cents (a partial bucket's true per-month rate, or a
// compounded premium); the shared `currency` formatter rounds to whole dollars,
// which would make the displayed formula not reproduce the row amount.
const exactCurrency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const money = (v: number) => (Math.abs(v % 1) < 0.005 ? currency(v) : exactCurrency.format(v));

// The partial bucket's months can be fractional (the engine keeps the exact
// remainder, e.g. 2.4 mo); show one decimal there, whole numbers elsewhere.
const monthsLabel = (m: number) => (Number.isInteger(m) ? `${m}` : m.toFixed(1));

const KIND_LABELS: Record<PremiumCashflowRow['kind'], string> = {
  lump: 'Lump sum',
  annual: 'Recurring premium',
  partial: 'Final partial year',
};

interface PolicyCostSectionProps {
  policy: PolicyFundingSource | null | undefined;
  // The active projection's claim terms — carries the shared
  // `monthsFromIssueToFirstPayout` anchor so this section can show the SAME
  // "benefits begin" line the monthly breakdown shows. Optional: omitted when no
  // projection is available (the anchor then simply doesn't render).
  terms?: ClaimTerms;
}

/**
 * Collapsible "Policy cost & premiums" section: the engine's premium cashflow
 * schedule (`investmentCashFlows`) rendered year by year with a per-row
 * formula, so the reader can reconstruct exactly how `policyTotalCost` was
 * determined. Premiums are shown ONLY here — the before-care and monthly
 * tables intentionally carry no premium column. Hidden when the policy has no
 * premium schedule.
 */
export function PolicyCostSection({ policy, terms }: PolicyCostSectionProps) {
  const schedule = useMemo(() => buildPremiumCashflowSchedule(policy), [policy]);

  if (!schedule.hasAny) return null;

  const subtitle = (
    <>
      <span className="font-semibold tabular-nums">{currency(schedule.totalPaid)}</span> total ·{' '}
      {schedule.rows.length} {schedule.rows.length === 1 ? 'payment' : 'payments'}
    </>
  );

  return (
    <CollapsibleSection title="Policy Cost & Premiums" subtitle={subtitle} defaultOpen={false}>
      {terms && <BenefitsBeginAnchor terms={terms} />}
      <InputsStrip schedule={schedule} />
      <div className="overflow-auto rounded-md border border-gray-200">
        <table className="min-w-full border-collapse text-xs">
          <thead className="bg-gray-50">
            <tr>
              <Th sticky>Policy year</Th>
              <Th>Type</Th>
              <Th
                tooltip={
                  <HeaderTooltip
                    description="How this row's amount is computed from the policy's premium inputs. Full years are monthly premium × 12; a pay period that isn't a whole number of years ends with a partial bucket of the remaining months; an annual increase rate compounds the monthly premium each payment year, with the exponent capped at the applied increase years."
                    formulas={[
                      { label: 'Full year', expression: 'monthly premium × 12' },
                      { label: 'Partial year', expression: 'monthly premium × remaining months' },
                      {
                        label: 'With increase',
                        expression:
                          'base monthly × (1 + rate)^min(payment year − 1, applied increase years) × months',
                      },
                    ]}
                  />
                }
              >
                Formula
              </Th>
              <Th align="right">Amount</Th>
              <Th
                align="right"
                tooltip={
                  <HeaderTooltip description="Running total paid into the policy through this row, inclusive." />
                }
              >
                Cumulative paid
              </Th>
            </tr>
          </thead>
          <tbody>
            {schedule.rows.map((row, i) => (
              // Index-keyed: a fractional applied-increase period can book a
              // full-year flow and the partial bucket at the SAME calendar
              // year, so kind+year alone can collide.
              <tr
                key={`${row.kind}-${row.calendarYear}-${i}`}
                className="border-b border-gray-100 bg-white hover:bg-gray-50"
              >
                <td className="sticky left-0 z-10 whitespace-nowrap bg-inherit px-3 py-2 align-top">
                  <div className="leading-tight">
                    <div className="font-medium text-gray-900">
                      {row.paymentYear != null ? `Year ${row.paymentYear}` : 'At issue'}
                    </div>
                    <div className="text-[10px] text-gray-500">{row.displayCalendarYear}</div>
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-2 align-top text-gray-700">
                  {KIND_LABELS[row.kind]}
                </td>
                <td className="whitespace-nowrap px-3 py-2 align-top">
                  <FormulaCell row={row} schedule={schedule} />
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right align-top font-medium text-gray-900">
                  {currency(row.amount)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right align-top tabular-nums text-gray-800">
                  {currency(row.cumulative)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
              <td className="sticky left-0 z-10 whitespace-nowrap bg-gray-50 px-3 py-2 text-sm text-gray-900">
                Total
              </td>
              <td />
              <td className="whitespace-nowrap px-3 py-2 text-[10px] font-normal text-gray-500">
                {totalFormula(schedule)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right text-gray-900">
                {currency(schedule.totalPaid)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-900">
                {currency(schedule.totalPaid)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <ReconciliationNote schedule={schedule} />
    </CollapsibleSection>
  );
}

// Reconciliation against the engine's canonical `policyTotalCost`. The engine
// derives that total by summing this exact stream, so a freshly calculated
// policy matches to the cent. Above the engine's own 1% validation tolerance
// (`investmentCashflowEqualsPolicyTotalCost`) it warns amber; smaller drift
// most likely means the stored total predates the latest calculation, surfaced
// neutrally so the two figures never silently disagree.
function ReconciliationNote({ schedule }: { schedule: PremiumCashflowSchedule }) {
  if (schedule.policyTotalCost == null) return null;
  const diff = schedule.totalPaid - schedule.policyTotalCost;
  if (schedule.reconciles === false) {
    return (
      <p className="mt-3 text-[11px] text-amber-700">
        These cashflows sum to {currency(schedule.totalPaid)}, which differs from the
        policy&rsquo;s recorded total cost of {currency(schedule.policyTotalCost)} by more than the
        1% tolerance. This indicates a data or calculation issue worth flagging.
      </p>
    );
  }
  if (Math.abs(diff) >= 1) {
    return (
      <p className="mt-3 text-[11px] text-gray-500">
        The policy&rsquo;s recorded total cost is {currency(schedule.policyTotalCost)} —{' '}
        {currency(Math.abs(diff))} {diff > 0 ? 'less' : 'more'} than these cashflows. The recorded
        total likely predates the latest calculation; recalculating the client realigns them.
      </p>
    );
  }
  return null;
}

// The Total row's one-line decomposition: "recurring premiums + lump sum",
// mirroring `policyTotalCost = monthlyPremiumTotalCost + policyLumpSumPayment`.
function totalFormula(schedule: PremiumCashflowSchedule): string {
  const lump = schedule.rows.filter(r => r.kind === 'lump').reduce((s, r) => s + r.amount, 0);
  const recurring = schedule.totalPaid - lump;
  if (lump > 0.5 && recurring > 0.5) {
    return `${currency(recurring)} premiums + ${currency(lump)} lump sum`;
  }
  return lump > 0.5 ? 'single lump sum' : 'sum of all premium years';
}

function FormulaCell({
  row,
  schedule,
}: {
  row: PremiumCashflowRow;
  schedule: PremiumCashflowSchedule;
}) {
  if (row.kind === 'lump') {
    return <span className="text-gray-700">single payment at issue</span>;
  }
  const { increaseRate, monthlyCost } = schedule.inputs;
  const compounded = (row.increaseExponent ?? 0) > 0 && increaseRate != null && monthlyCost != null;
  return (
    <div className="leading-tight">
      <div className="text-gray-700">
        {row.monthlyRate != null ? `${money(row.monthlyRate)}/mo` : '—'} ×{' '}
        {row.monthsCovered != null ? monthsLabel(row.monthsCovered) : '—'} mo
      </div>
      {compounded && (
        <div className="text-[10px] text-gray-500">
          {money(monthlyCost)}/mo × (1 + {formatValue(increaseRate, 'percent')})
          <sup>{row.increaseExponent}</sup>
        </div>
      )}
    </div>
  );
}

// Compact strip of the raw premium inputs above the table, so the per-row
// formulas can be traced back to the policy fields that drive them.
function InputsStrip({ schedule }: { schedule: PremiumCashflowSchedule }) {
  const { monthlyCost, lumpSum, periodMonths, limitedPayYears, increaseRate } = schedule.inputs;
  // A monthly cost with no pay period produces no premium rows (lump-only
  // schedule) — showing the unused input would imply it contributes to the
  // total.
  const hasPremiumRows = schedule.rows.some(r => r.kind !== 'lump');
  const items: Array<{ label: string; value: string }> = [];
  if (monthlyCost != null && monthlyCost > 0 && hasPremiumRows) {
    items.push({ label: 'Monthly premium', value: `${money(monthlyCost)}/mo` });
  }
  if (lumpSum != null && lumpSum > 0) {
    items.push({ label: 'Lump sum', value: currency(lumpSum) });
  }
  if (periodMonths != null && periodMonths > 0) {
    const years = periodMonths / 12;
    items.push({
      label: 'Pay period',
      value: `${Number.isInteger(years) ? years : years.toFixed(1)} yrs (${Math.round(periodMonths)} mo)${
        limitedPayYears ? ` · ${limitedPayYears}-pay` : ''
      }`,
    });
  }
  if (increaseRate != null && increaseRate > 0) {
    // Show the whole step-up years actually applied (the engine floors a
    // fractional applied-increase period), not the raw fractional input.
    items.push({
      label: 'Annual increase',
      value: `${formatValue(increaseRate, 'percent')}${
        schedule.stepUpYears != null ? ` for ${schedule.stepUpYears} yrs` : ''
      }`,
    });
  }
  if (items.length === 0) return null;
  return (
    <dl className="mb-3 flex flex-wrap gap-x-6 gap-y-1">
      {items.map(item => (
        <div key={item.label} className="flex items-baseline gap-1.5 text-xs">
          <dt className="text-gray-500">{item.label}</dt>
          <dd className="font-medium text-gray-900">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Th({
  children,
  sticky,
  align,
  tooltip,
}: {
  children: string;
  sticky?: boolean;
  align?: 'right';
  tooltip?: React.ReactNode;
}) {
  return (
    <th
      className={`whitespace-nowrap border-b border-gray-200 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-700 ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${sticky ? 'sticky left-0 z-10 bg-gray-50' : ''}`}
    >
      {tooltip ? <TooltipLabel label={children} tooltip={tooltip} /> : children}
    </th>
  );
}
